import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Trash2, Plus, Save, X, Loader2 } from "lucide-react";
import { mailboxApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { useMailboxTopics, reloadMailboxTopics } from "./useMailboxTopics";

const errMsg = (e, f) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : f);

function TopicForm({ form, setForm, onSave, onCancel, saving }) {
  return (
    <div className="rounded-lg border border-primary/40 p-3 space-y-2" data-testid="topic-form">
      <Input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="topic-form-name" />
      <Input placeholder="Beschreibung für die KI, z. B. „Software-Updates und Wartungsarbeiten“" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="topic-form-description" />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel}><X className="w-4 h-4 mr-1" />Abbrechen</Button>
        <Button size="sm" onClick={onSave} disabled={saving} data-testid="topic-form-save">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Speichern</Button>
      </div>
    </div>
  );
}

export function TopicsManager({ open, onOpenChange, onChanged }) {
  const toast = useToast();
  const topics = useMailboxTopics();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Bitte einen Namen eingeben");
    setSaving(true);
    try {
      if (editing === "new") await mailboxApi.createTopic(form);
      else await mailboxApi.updateTopic(editing, form);
      await reloadMailboxTopics();
      toast.success("Thema gespeichert – Mails werden im Hintergrund neu eingeordnet");
      setEditing(null);
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, "Speichern fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Thema „${t.name}“ löschen? Die Mails darin werden neu eingeordnet.`)) return;
    try {
      await mailboxApi.deleteTopic(t.id);
      await reloadMailboxTopics();
      toast.success("Thema gelöscht");
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, "Löschen fehlgeschlagen"));
    }
  };

  const formProps = { form, setForm, onSave: save, onCancel: () => setEditing(null), saving };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="topics-manager-dialog">
        <DialogHeader>
          <DialogTitle>Themen verwalten</DialogTitle>
          <DialogDescription>Die KI ordnet jede Mail einem dieser Themen zu. Die Beschreibung hilft ihr dabei. Von Hand gesetzte Themen bleiben erhalten.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {topics.map((t) => editing === t.id ? <TopicForm key={t.id} {...formProps} /> : (
            <div key={t.id} className="flex items-start gap-2 rounded-lg border px-3 py-2" data-testid={`topic-row-${t.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{t.name}</p>
                {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => { setEditing(t.id); setForm({ name: t.name, description: t.description || "" }); }} data-testid={`topic-edit-${t.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid={`topic-delete-${t.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          ))}
          {editing === "new" ? <TopicForm {...formProps} /> : (
            <Button variant="outline" className="w-full" onClick={() => { setEditing("new"); setForm({ name: "", description: "" }); }} data-testid="topic-add-btn"><Plus className="w-4 h-4 mr-1.5" />Thema hinzufügen</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
